export const MAX_UPLOAD_FILE_SIZE = 50 * 1024 * 1024

const SYSTEM_SUPPORTED_EXTENSIONS = Object.freeze([
  'docx',
  'xlsx',
  'pptx',
  'pdf',
  'epub',
  'srt',
  'ass',
  'vtt'
])

/**
 * @typedef {{
 *   size?: number
 *   name?: string
 * }} UploadFileLike
 */

/**
 * @typedef {{
 *   maxSizeBytes?: number
 *   allowedExtensions?: string[]
 * }} UploadFileValidationOptions
 */

/**
 * @param {string[] | null | undefined} extensions
 * @returns {string[]}
 */
function normalizeExtensionList(extensions) {
  if (!Array.isArray(extensions)) {
    return []
  }

  return extensions
    .map(ext => String(ext || '').toLowerCase().replace(/^\./, ''))
	    .filter(Boolean)
}

/**
 * @param {string | undefined | null} fileName
 * @returns {string}
 */
export function getFileExtension(fileName) {
  if (typeof fileName !== 'string') {
    return ''
  }

  const segments = fileName.split('.')
  if (segments.length <= 1) {
    return ''
  }

	return String(segments.pop() || '').toLowerCase()
}

/**
 * @param {UploadFileLike | null | undefined} file
 * @param {UploadFileValidationOptions} [options]
 */
export function validateUploadFile(file, options = {}) {
  if (!file) {
    return { ok: false, reason: 'missing_file' }
  }

  const maxSizeBytes = Number(options.maxSizeBytes) > 0
    ? Number(options.maxSizeBytes)
    : MAX_UPLOAD_FILE_SIZE

  if (Number(file.size) > maxSizeBytes) {
    return {
      ok: false,
      reason: 'too_large',
      maxSizeBytes
    }
  }

  const ext = getFileExtension(file.name)
  const allowedExtensions = normalizeExtensionList(options.allowedExtensions)

  if (allowedExtensions.length > 0 && !allowedExtensions.includes(ext)) {
    return {
      ok: false,
      reason: 'allowed_extension_mismatch',
      ext,
      allowedExtensions
    }
  }

  if (!SYSTEM_SUPPORTED_EXTENSIONS.includes(ext)) {
    return {
      ok: false,
      reason: 'unsupported_format',
      ext
    }
  }

  return {
    ok: true,
    reason: 'valid',
    ext
	  }
}

/**
 * @param {{
 *   validation?: {
 *     ok?: boolean
 *     reason?: string
 *     ext?: string
 *   } | null
 *   file?: UploadFileLike
 *   allowedExtensions?: string[]
 * }} [params]
 */
export function resolveFileValidationAction(params = {}) {
  const { validation, file, allowedExtensions = [] } = params

  if (!validation || validation.ok !== true) {
    const reason = validation?.reason
    if (reason === 'too_large') {
      return {
        shouldEmitFileSelected: false,
        shouldEmitFileTypeMismatch: false,
        errorType: 'too_large'
      }
    }

    if (reason === 'allowed_extension_mismatch') {
      return {
        shouldEmitFileSelected: false,
        shouldEmitFileTypeMismatch: true,
        file,
        errorType: 'allowed_extension_mismatch',
        ext: validation?.ext || '',
        allowedExtensions
      }
    }

    if (reason === 'unsupported_format') {
      return {
        shouldEmitFileSelected: false,
        shouldEmitFileTypeMismatch: false,
        errorType: 'unsupported_format'
      }
    }

    return {
      shouldEmitFileSelected: false,
      shouldEmitFileTypeMismatch: false,
      errorType: 'failed'
    }
  }

  return {
    shouldEmitFileSelected: true,
    shouldEmitFileTypeMismatch: false,
    file,
    errorType: null
	  }
}

/**
 * @param {{
 *   file?: UploadFileLike
 *   allowedExtensions?: string[]
 *   maxSizeBytes?: number
 * }} [params]
 */
export function evaluateFileSelectionContract(params = {}) {
  const {
    file,
    allowedExtensions = [],
    maxSizeBytes = MAX_UPLOAD_FILE_SIZE
  } = params

  const validation = validateUploadFile(file, {
    allowedExtensions,
    maxSizeBytes
  })

  const action = resolveFileValidationAction({
    validation,
    file,
    allowedExtensions
  })

  return {
    validation,
    action,
    shouldEnterUploadChain: action.shouldEmitFileSelected === true,
    shouldEmitFileSelected: action.shouldEmitFileSelected,
    shouldEmitFileTypeMismatch: action.shouldEmitFileTypeMismatch,
    errorType: action.errorType,
    ext: action.ext || validation.ext || ''
  }
}
